import { useCallback, useEffect, useRef, useState } from 'react';

export const SACRAMENTO_FALLBACK = Object.freeze({
  latitude: 38.5816,
  longitude: -121.4944,
  postcode: '95814',
  city: 'Sacramento',
  state: 'CA',
});

const GEOLOCATION_TIMEOUT_MS = 8000;

export function useGeolocation({ autoRequest = true } = {}) {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | requesting | granted | denied | unsupported | error | fallback
  const [error, setError] = useState(null);
  const triggeredRef = useRef(false);

  const applyFallback = useCallback((reason) => {
    setCoords({
      latitude: SACRAMENTO_FALLBACK.latitude,
      longitude: SACRAMENTO_FALLBACK.longitude,
      postcode: SACRAMENTO_FALLBACK.postcode,
      city: SACRAMENTO_FALLBACK.city,
      state: SACRAMENTO_FALLBACK.state,
      isFallback: true,
    });
    setStatus('fallback');
    setError(reason || null);
  }, []);

  const request = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      applyFallback({ code: 'unsupported', message: 'Geolocation API not available' });
      setStatus('unsupported');
      return;
    }

    setStatus('requesting');
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          isFallback: false,
        });
        setStatus('granted');
      },
      (err) => {
        const denied = err && err.code === err.PERMISSION_DENIED;
        applyFallback({ code: denied ? 'denied' : 'error', message: err?.message });
        if (denied) setStatus('denied');
        else setStatus('error');
      },
      { enableHighAccuracy: false, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 60_000 }
    );
  }, [applyFallback]);

  useEffect(() => {
    if (autoRequest && !triggeredRef.current) {
      triggeredRef.current = true;
      request();
    }
  }, [autoRequest, request]);

  return {
    coords,
    status,
    error,
    request,
    isFallback: coords?.isFallback === true,
  };
}

export default useGeolocation;
